import { ParticipanteResponse, ParticipantesFilter, RegistroHistoricoResponse, RegistroStatus } from 'common/genericTypes';
import { MdSpace, MoreOptionsMenu, TextButton } from 'm2m.components';
import {
  GenericDrawer,
  GenericDrawerButtonBehaviour,
  GenericDrawerDocumentBehaviour,
  GenericDrawerTextContent,
} from 'components/genericDrawer';
import moment from 'moment';
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { RootState } from 'store';
import { componentsPropsValues, localizedTextsMap, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import { Tooltip, MenuItem } from '@material-ui/core';
import { WatchLater, Cancel, CheckCircle, VerifiedUser, Description } from '@material-ui/icons';
import { useGenericStyles } from 'common/genericStyles';
import {
  funcaoParticipanteDescriptions,
  statusFormularioDescriptions,
  tipoAcessoParticipanteDescriptions,
} from 'common/genericDescriptions';
import { addCpfCnpjMask } from 'utils/mask';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import {
  DataGridPro,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridCellParams,
  GridFilterModel,
  GridSortModel,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro';

interface Props {
  filtros?: string;
}

const Participantes: React.FC<Props> = (props) => {
  const { filtros } = props;
  const dispatch = useDispatch();
  const history = useHistory();

  const { getParticipante: getParticipanteApi, postParticipantePaginated } = useSelector(
    (state: RootState) => state.participanteApi
  );

  const classes = useGenericStyles();

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [pageCount, setPageCount] = useState<number>(0);
  const [participantes, setParticipantes] = useState<ParticipanteResponse[]>([]);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [participantesFilter, setParticipanteFilter] = useState<Array<ParticipantesFilter>>([]);
  const [linkOperator, setLinkOperator] = useState<any>('AND');

  const [openJustificativaDrawer, setOpenJustificativaDrawer] = useState<boolean>(false);
  const [justificativaTextContents, setJustificativaTextContents] = useState<Array<GenericDrawerTextContent>>([]);
  const [justificativaDocuments, setJustificativaDocuments] = useState<
    Array<GenericDrawerDocumentBehaviour> | undefined
  >(undefined);
  const [justificativaButtons, setJustificativaButtons] = useState<Array<GenericDrawerButtonBehaviour>>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const { getRegistroDocumentDownloadLink } = useSelector((state: RootState) => state.registroApi);

  const getParticipantes = useCallback(async () => {
    try {
      setShowLoader(true);
      const result = await postParticipantePaginated(
        dispatch,
        { linkOperator: linkOperator, page: page + 1, pageSize, FilterTypeParticipante: participantesFilter },
        true
      );
      setParticipantes(result.result);
      setPageCount(result.totalCount);
      setShowLoader(false);
    } catch {
      setShowLoader(false);
    }
  }, [dispatch, postParticipantePaginated, participantesFilter, pageSize, page, linkOperator]);

  useEffect(() => {
    getParticipantes();
  }, [getParticipantes, page, participantesFilter]);

  const onFilterChange = useCallback(
    (filterModel: GridFilterModel) => {
      if (!filterModel.items || filterModel.items.length === 0) {
        setParticipanteFilter([]);
        setPage(0);
        return;
      }
      setLinkOperator(filterModel?.linkOperator?.toString()?.toUpperCase() ?? 'AND');
      const participanteFilter = new Array<ParticipantesFilter>();

      filterModel.items.forEach((filter) => {
        let operatorValue =
          filter.operatorValue == 'startsWith'
            ? 'STARTS_WITH'
            : filter.operatorValue == 'endsWith'
            ? 'ENDS_WITH'
            : filter.operatorValue?.toUpperCase();

        let formattedValue = filter.value?.toString();
        if (formattedValue && filter.columnField) {
          let params = {
            columnType: filter.columnField.toUpperCase(),
            operationFilterType: operatorValue,
            value: formattedValue,
          } as ParticipantesFilter;

          participanteFilter.push(params);
        }
      });

      setParticipanteFilter(participanteFilter);
      setPage(0);
    },
    [getParticipantes, page, pageSize, linkOperator, participantesFilter]
  );

  const onSortChange = useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel == null || sortModel.length === 0) {
        const newFilter = participantesFilter.filter(
          (x) => x.orderByType != 'ASC' && x.orderByType != 'DESC'
        );
        setParticipanteFilter([...newFilter]);
        return;
      }

      const filter = sortModel[0];
      let params = {
        columnType: filter.field.toUpperCase(),
        orderByType: filter.sort?.toUpperCase(),
      } as ParticipantesFilter;

      const newFilter = participantesFilter.filter(
        (x) => x.orderByType != 'ASC' && x.orderByType != 'DESC'
      ) as Array<ParticipantesFilter>;
      newFilter.push(params);
      setParticipanteFilter([...newFilter]);
    },
    [getParticipantes, page, pageSize, participantesFilter]
  );

  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ['equals', 'contains', 'startsWith', 'endsWith'].includes(value)
  );

  const filterOperatorsSelect = getGridSingleSelectOperators().filter(({ value }) =>
    ['is', 'not'].includes(value)
  );

  const onDetalhesFunction = async (participante: ParticipanteResponse) => {
    return history.push(`/gerenciar/participantes/detalhes/${participante.id}`);
  };

  const getStatusIcon = useCallback(
    (status: RegistroStatus) => {
      switch (status) {
        case RegistroStatus.EmAnalise:
          return <WatchLater fontSize="small" className={classes.warningIcon} />;
        case RegistroStatus.Aprovado:
          return <CheckCircle fontSize="small" className={classes.successIcon} />;
        case RegistroStatus.Reprovado:
          return <Cancel fontSize="small" className={classes.errorIcon} />;
        case RegistroStatus.AguardandoCorrecao:
          return <Cancel fontSize="small" className={classes.waitingIcon} />;
        case RegistroStatus.Excluido:
          return <Cancel fontSize="small" className={classes.warningIcon} />;
        case RegistroStatus.AguardandoAprovacaoEdicao:
          return <Cancel fontSize="small" className={classes.waitingIcon} />;
        case RegistroStatus.AguardandoAprovacaoAditamento:
          return <Cancel fontSize="small" className={classes.waitingIcon} />;
      }
    },
    [classes]
  );

  const sortByDataDeCriacaoDesc = (hist: Array<RegistroHistoricoResponse>): Array<RegistroHistoricoResponse> =>
    hist.sort((a, b) => +moment(b.dataDeCriacao) - +moment(a.dataDeCriacao));

  const showJustificativaButton = (status: RegistroStatus): boolean =>
    status === RegistroStatus.AguardandoCorrecao || status === RegistroStatus.Reprovado;

  const getParticipante = async (id: string): Promise<ParticipanteResponse | null> => {
    try {
      const result = await getParticipanteApi(dispatch, id, true);
      return result;
    } catch (error) { }
    return null;
  };

  const getRegistroHistoricoDownloadLink = async (documentId: string): Promise<string> => {
    let result: string = '';
    try {
      result = await getRegistroDocumentDownloadLink(dispatch, documentId, true);
    } catch { }
    return result;
  };

  const handleDownloadRegistroHistoricoDocument = async (documentId: string, name: string) => {
    const url = await getRegistroHistoricoDownloadLink(documentId);
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const clearJustificativaDrawer = () => {
    setJustificativaTextContents([]);
    setJustificativaButtons([]);
    setJustificativaDocuments(undefined);
  };

  const handleCloseJustificativaDrawer = () => {
    setOpenJustificativaDrawer(false);
    setLoading(false);
  };

  const handleOpenJustificativaDrawer = async (participanteId: string) => {
    setLoading(true);
    clearJustificativaDrawer();

    const detalhes = await getParticipante(participanteId);
    if (detalhes?.registro.registroHistoricos?.length) {
      const sortedHistoricos = sortByDataDeCriacaoDesc(detalhes.registro.registroHistoricos);
      const latestHistorico = sortedHistoricos[0];

      setJustificativaTextContents([{ subtitle: 'Justificativa:', text: latestHistorico.justificativa }]);
      let buttons: Array<GenericDrawerButtonBehaviour> = [];
      buttons.push({ buttonText: 'Cancelar', buttonAction: handleCloseJustificativaDrawer });
      setJustificativaButtons(buttons);

      if (latestHistorico.registroHistoricoDocuments) {
        const docs = latestHistorico.registroHistoricoDocuments.map((doc) => {
          let base = doc as GenericDrawerDocumentBehaviour;
          base.downloadDocument = async () => await handleDownloadRegistroHistoricoDocument(base.id, base.fileName);
          return base;
        });
        setJustificativaDocuments(docs);
      }
      setOpenJustificativaDrawer(true);
    } else {
      handleCloseJustificativaDrawer();
    }
    setLoading(false);
  };

  const onMainButtonClick = (participante: ParticipanteResponse) => {
    return showJustificativaButton(participante.registro.status)
      ? () => handleOpenJustificativaDrawer(participante.id)
      : undefined;
  };

  const getGridColumns = useCallback(
    () => [
      {
        field: 'STATUS',
        headerName: 'Status',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 200,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => (params.row as ParticipanteResponse).registro.status,
        renderCell: (params: GridCellParams) => {
          const participante = params.row as ParticipanteResponse;
          return (
            <>
              <Tooltip
                title={statusFormularioDescriptions[participante.registro.status]}
                aria-label={statusFormularioDescriptions[participante.registro.status]}
              >
                {getStatusIcon(participante.registro.status)}
              </Tooltip>
              {onMainButtonClick(params.row as ParticipanteResponse) && (
                <TextButton
                  style={{ marginLeft: '15px' }}
                  className={classes.textButton}
                  onClick={onMainButtonClick(params.row as ParticipanteResponse)}
                >
                  {`Ver Justificativa`}
                </TextButton>
              )}
            </>
          );
        },
      },
      {
        field: 'avaliadoPeloKyc',
        headerName: 'Avalição CAF',
        type: 'singleSelect',
        valueOptions: ['Avaliado'],
        filterOperators: filterOperatorsSelect,
        filterable: true,
        sortable: true,
        disableExport: false,
        valueGetter: (params: GridValueGetterParams) => (params.row as ParticipanteResponse).avaliadoPeloKyc,
        renderCell: (params: GridCellParams) => {
          const participante = params.row as ParticipanteResponse;
          return (
            <>
              {participante.avaliadoPeloKyc && (
                <Tooltip title={'Avaliado pelo CAF'} aria-label={'Avaliado pelo CAF'}>
                  {<VerifiedUser fontSize="small" color="action" />}
                </Tooltip>
              )}
            </>
          );
        },
        width: 115,
        minWidth: 100,
        maxWidth: 200,
      },
      {
        field: 'RAZAO_SOCIAL',
        headerName: 'Razão Social Participante',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) => (params.row as ParticipanteResponse).razaoSocial,
        width: 220,
        minWidth: 150,
        maxWidth: 200,
      },
      {
        field: 'CNPJ',
        headerName: 'CNPJ Participante',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        valueGetter: (params: GridValueGetterParams) => addCpfCnpjMask((params.row as ParticipanteResponse).cnpj),
      },
      {
        field: 'TIPO_ACESSO',
        headerName: 'Tipo de Acesso',
        filterOperators,
        filterable: false,
        sortable: false,
        valueGetter: (params: GridValueGetterParams) =>
          tipoAcessoParticipanteDescriptions[(params.row as ParticipanteResponse).tipoAcesso],
        width: 150,
        minWidth: 150,
        maxWidth: 200,
      },
      {
        field: 'ATIVIDADE_PRINCIPAL_DESENVOLVIDA',
        headerName: 'Atividade Principal Desenvolvida',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as ParticipanteResponse).atividadePrincipalDesenvolvida,
        width: 200,
        minWidth: 150,
        maxWidth: 200,
      },
      {
        field: 'FUNCAO',
        headerName: 'Funções do Participante',
        filterOperators,
        filterable: true,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as ParticipanteResponse).funcoes
            .map((pf) => funcaoParticipanteDescriptions[pf.funcao])
            .join(', '),
        width: 200,
        minWidth: 150,
        maxWidth: 200,
      },
      {
        field: 'FATURAMENTO_MEDIOMENSAL_ULTIMOS_DOZE_MESES',
        headerName: 'Faturamento Médio Mensal',
        filterable: false,
        sortable: false,
        valueGetter: (params: GridValueGetterParams) =>
          (params.row as ParticipanteResponse).faturamentoMedioMensalUltimosDozeMeses,
        width: 175,
        minWidth: 150,
        maxWidth: 200,
      },
      {
        field: 'actions',
        headerName: ' ',
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params: GridCellParams) => (
          <MoreOptionsMenu>
            {onDetalhesFunction && (
              <MenuItem
                onClick={() => {
                  onDetalhesFunction(params.row as ParticipanteResponse);
                }}
              >
                <Description fontSize="small" style={{ marginRight: '10px' }} />
                {'Ver Detalhes'}
              </MenuItem>
            )}
          </MoreOptionsMenu>
        ),
        width: 75,
        minWidth: 75,
        maxWidth: 200,
      },
    ],
    []
  );

  return (
    <>
      <MdSpace />
      <DataGridPro
        autoHeight
        localeText={localizedTextsMap}
        componentsProps={componentsPropsValues}
        rows={participantes}
        columns={getGridColumns()}
        components={{
          Toolbar: CustomToolBarUtils,
        }}
        disableSelectionOnClick
        rowsPerPageOptions={rowsPerPageOptionsValues}
        page={page}
        rowCount={pageCount}
        pagination
        paginationMode="server"
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        pageSize={pageSize}
        loading={showLoader}
        filterMode="server"
        sortingMode="server"
        onFilterModelChange={onFilterChange}
        onSortModelChange={onSortChange}
      />
      <MdSpace />
      <div style={{ paddingTop: 40 }}>
        <MdSpace />
        <GenericDrawer
          opened={openJustificativaDrawer}
          title={'Formulário Rejeitado'}
          downloadDocuments={justificativaDocuments}
          textContents={justificativaTextContents}
          onClose={handleCloseJustificativaDrawer}
          buttons={justificativaButtons}
          disableButtons={loading}
        />
      </div>
    </>
  );
};

export default Participantes;
